import React, { useEffect, useRef, useState } from 'react';
import { animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import TimelineSlideContainer from './TimelineSlideContainer';
import styled from 'styled-components';
import { getYearsFlat, scrollSlideIntoView, setSlideFocus, useMobile, getYearPrevOrNext, } from './Timeline._helpers';
import { media, SCREEN_WIDTH, isWindowMobile } from '../../utils/media';
import { COLOURS, MOBILE_SCROLL } from './Timeline._constants';
import { debounced } from '../../utils/helpers';
import { isMobileOnly } from 'react-device-detect';
const Container = styled.div `
  height: calc(100vh - 74px);
  position: relative;
  overflow-y: hidden;
  overflow-anchor: none;

  //scroll bar
  -ms-overflow-style: none; // IE 10+
  overflow: -moz-scrollbars-none; // Firefox
  scrollbar-width: none; // Firefox
  &::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }

  ${media.tablet} {
    height: ${({ containerHeight }) => (containerHeight ? containerHeight + `px` : `0px`)};
  }

  ${media.mobile} {
    height: 84%; // ie
    height: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .slideContainer {
    height: 100%;
  }
  .slideContainer:first-of-type {
    border-top: unset;
  }
`;
const ContainerHighlightYearArrow = styled.div `
  ${(props) => {
    var _a;
    return ((_a = props) === null || _a === void 0 ? void 0 : _a.isMobile) ? `
  position: relative;
  left: 50%;
  width: 1px;
  height: 1px;
  background-color: transparent;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 50%;
    bottom: 0px;
    margin: -5px 0 0 -5px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${COLOURS.RED_10};
    border-left: 6px solid transparent;
  }
`
        : '';
}}
`;
const TimelineContent = ({ setHighlightYear, handleHeight, clickYear, years, contentWidth, setYear, setId, handleClick, containerHeight, }) => {
    let init = 0;
    const [yearsFlat, setYearsFlat] = useState([]);
    const [currentChildPosition, updateChildPosition] = useState(0);
    const scrollRef = useRef(null); // const childRefs = useRef([...Array(getYearsCount(years))].map(() => createRef()));
    const ms = 75;
    const cs = scrollRef.current;
    let scrollTop = cs && cs.scrollTop;
    useEffect(() => {
        if (!init) {
            setYearsFlat(getYearsFlat(years));
            ++init;
        }
    }, []);
    const moveToSlideNextOrPrev = (direction) => {
        const yearGet = getYearPrevOrNext(yearsFlat, { year: setYear, id: setId }, direction === MOBILE_SCROLL.DIR_PREV ? -1 : 1);
        if (yearGet && yearGet.id !== setId) {
            handleClick(yearGet.year, yearGet.id);
            setHighlightYear(yearGet.year, yearGet.id);
        }
    };
    const bind = useDrag(({ last, vxvy: [vx, vy] }) => {
        if (last) {
            if (!isMobileOnly && Math.abs(vx) < Math.abs(vy)) {
                if (vy !== 0 && vy > 0) {
                    moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_PREV);
                }
                else if (vy < 0) {
                    moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_NEXT);
                }
            }
            else if (isMobileOnly && Math.abs(vx) > Math.abs(vy)) {
                if (vx < 0) {
                    moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_NEXT);
                }
                else if (vx > 0) {
                    moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_PREV);
                }
            }
        }
    });
    useEffect(() => {
        if (cs) {
            if (scrollTop !== currentChildPosition) {
                updateChildPosition(cs.scrollTop);
            }
        }
    }, [scrollTop]); // scroll, resize
    useEffect(() => {
        // this useEffect handles events, esp. user scrolling
        let timeoutResize;
        const resizeFn = () => {
            if (typeof window === 'undefined') {
                return;
            }
            clearTimeout(timeoutResize);
            timeoutResize = setTimeout(() => {
                const dims = cs.getBoundingClientRect();
                handleHeight(Math.floor(dims.width), Math.floor(dims.height));
            }, ms);
        };
        const wheelFn = (e) => {
            if (typeof window === undefined) {
                return;
            }
            if (e.deltaY < 0) {
                return moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_PREV);
            }
            else if (e.deltaY > 0) {
                return moveToSlideNextOrPrev(MOBILE_SCROLL.DIR_NEXT);
            }
        };
        const debouncedWheelFn = debounced(300, wheelFn);
        const cs = scrollRef.current;
        if (cs) {
            resizeFn();
            if (!isWindowMobile()) {
                cs.addEventListener('wheel', debouncedWheelFn);
            }
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', resizeFn);
            }
        }
        return () => {
            if (cs) {
                if (!isWindowMobile()) {
                    cs.removeEventListener('wheel', debouncedWheelFn);
                }
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', resizeFn);
                }
            }
        };
    });
    useEffect(() => {
        // this useEffect handles user clicking year in timeline
        const scrollSlideIntoViewFn = debounced(300, scrollSlideIntoView);
        if (clickYear.doScrollFinish) {
            scrollSlideIntoViewFn(clickYear.year, clickYear.id);
        }
    }, [clickYear]);
    const slides = years &&
        years.length > 0 &&
        years.map((yearSet) => {
            if (!yearSet || !yearSet.items || !yearSet.items.length) {
                return;
            }
            return yearSet.items.map((item, i) => {
                return (React.createElement(animated.div, Object.assign({}, bind(), { className: "slideContainer", key: i }),
                    React.createElement(TimelineSlideContainer, { years: { decade: yearSet.decade, year: item.year, id: item.id }, index: i, fields: item.fields, contentWidth: contentWidth })));
            });
        });
    let listenScroll;
    const handleListenScroll = () => {
        // @ts-ignore
        clearTimeout(listenScroll);
        listenScroll = setTimeout(() => {
            setSlideFocus(clickYear.id, contentWidth);
        }, ms);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { id: "content", ref: scrollRef, onScroll: handleListenScroll, containerHeight: containerHeight }, slides),
        React.createElement(ContainerHighlightYearArrow, { className: "redArrow", isMobile: useMobile(contentWidth, SCREEN_WIDTH.MOBILE) })));
};
export default TimelineContent;
